import React, { useState } from 'react';
import { object, func, bool, string } from 'prop-types';

import { useConfiguration } from '../../context/configurationContext';
import FilterComponent from '../../containers/SearchPage/FilterComponent';
import IconPlus from '../../containers/SearchPage/IconPlus/IconPlus';
import {
  STORE_SIDEBAR_CATEGORIES,
  getSubcategoriesForCategory,
  normalizeLegacySubcategorySlug,
} from '../../util/storeTaxonomy';

import css from './StoreSidebar.module.css';

const CategoryButtonFilter = ({ label, options, urlQueryParams, onParamChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selected = urlQueryParams.pub_category || null;

  const handleClick = (value) => {
    if (selected === value) {
      onParamChange({ pub_category: null, pub_subcategory: null });
    } else {
      onParamChange({ pub_category: value, pub_subcategory: null });
    }
  };

  return (
    <div className={css.categoryFilter}>
      <div className={css.categoryFilterHeader}>
        <button type="button" className={css.categoryFilterLabel} onClick={() => setIsOpen(prev => !prev)}>
          <span>{label}</span>
          <span className={css.categoryFilterToggle}><IconPlus isOpen={isOpen} /></span>
        </button>
      </div>
      {isOpen && (
        <div className={css.categoryButtonGroup}>
          {options.map(({ option, label: optLabel }) => (
            <button
              key={option}
              type="button"
              className={selected === option ? `${css.categoryButton} ${css.categoryButtonSelected}` : css.categoryButton}
              onClick={() => handleClick(option)}
            >
              {optLabel}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const HARDCODED_SIDEBAR_FILTERS = {
  price: {
    key: 'price',
    schemaType: 'price',
    min: 0,
    max: 5000,
    step: 5,
    filterConfig: { label: 'Price' },
  },
  condition: {
    key: 'condition',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'New', label: 'New' },
      { option: 'Used', label: 'Used' },
      { option: 'Open Box', label: 'Open Box' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Condition',
      group: 'primary',
    },
  },
  deliveryOption: {
    key: 'deliveryOption',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'shipping', label: 'Shipping' },
      { option: 'pickup', label: 'Pickup / In-Store' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Delivery Option',
      searchMode: 'has_any',
      group: 'primary',
    },
  },
};

const PRODUCT_SIDEBAR_FILTER_ORDER = [
  { key: 'category', label: 'Interest Area' },
  { key: 'subcategory', label: 'Sub Category' },
  { key: 'price', label: 'Price' },
  { key: 'brand', label: 'Brand' },
  { key: 'condition', label: 'Condition' },
  { key: 'deliveryOption', label: 'Delivery Option' },
];

const SERVICE_SIDEBAR_FILTER_ORDER = [{ key: 'price', label: 'Price' }];

const getSidebarFilterConfig = (key, label, availableByKey, currentCategory) => {
  if (key === 'subcategory') {
    return {
      key: 'subcategory',
      scope: 'public',
      schemaType: 'enum',
      enumOptions: getSubcategoriesForCategory(currentCategory),
      filterConfig: { label, filterType: 'SelectMultipleFilter', group: 'primary' },
    };
  }

  if (key === 'category') {
    return {
      key: 'category',
      scope: 'public',
      schemaType: 'enum',
      enumOptions: STORE_SIDEBAR_CATEGORIES,
      filterConfig: { label, filterType: 'SelectMultipleFilter', group: 'primary' },
    };
  }

  const field =
    availableByKey[key] || HARDCODED_SIDEBAR_FILTERS[key];
  if (!field) return null;
  if (field.schemaType !== 'price' && !field.enumOptions?.length) return null;
  return { ...field, filterConfig: { ...field.filterConfig, label } };
};

export const getStoreSidebarFilters = (
  availableFilters,
  currentCategory = null,
  catalogMode = 'products'
) => {
  const availableByKey = (availableFilters || []).reduce((acc, filterConfig) => {
    return filterConfig?.key ? { ...acc, [filterConfig.key]: filterConfig } : acc;
  }, {});

  const filterOrder =
    catalogMode === 'services' ? SERVICE_SIDEBAR_FILTER_ORDER : PRODUCT_SIDEBAR_FILTER_ORDER;

  const orderedFilters = filterOrder
    .map(({ key, label }) => getSidebarFilterConfig(key, label, availableByKey, currentCategory))
    .filter(Boolean);

  const orderedKeys = new Set(orderedFilters.map(filterConfig => filterConfig.key));
  const remainingFilters =
    catalogMode === 'services'
      ? []
      : (availableFilters || []).filter(
          filterConfig => filterConfig?.key && !orderedKeys.has(filterConfig.key)
        );

  return [...orderedFilters, ...remainingFilters];
};

export const mergeWithStoreSidebarFilters = (
  availableFilters,
  currentCategory = null,
  catalogMode = 'products'
) => getStoreSidebarFilters(availableFilters, currentCategory, catalogMode);

const StoreSidebar = ({
  urlQueryParams,
  onParamChange,
  onResetAll,
  intl,
  isMobile,
  catalogMode,
}) => {
  const config = useConfiguration();
  const listingFieldsConfig = config?.listing?.listingFields || [];
  const currentCategory = catalogMode === 'services' ? null : urlQueryParams.pub_category || null;

  const sidebarFilters = getStoreSidebarFilters(listingFieldsConfig, currentCategory, catalogMode);

  const getInitialValues = (queryParamNames) => {
    if (!Array.isArray(queryParamNames)) return {};
    return queryParamNames.reduce((acc, paramName) => {
      const val = urlQueryParams[paramName];
      const nextValue =
        paramName === 'pub_subcategory' && val != null
          ? normalizeLegacySubcategorySlug(String(val))
          : val != null
          ? String(val)
          : val;
      return { ...acc, [paramName]: nextValue };
    }, {});
  };

  const getHandleChangedValueFn = (useHistoryPush) => {
    return (updatedURLParams) => {
      if (useHistoryPush) {
        onParamChange(updatedURLParams);
      }
    };
  };

  return (
    <aside className={isMobile ? css.sidebarMobile : css.sidebar}>
      {sidebarFilters.map(filterConfig => {
        if (filterConfig.key === 'category') {
          return (
            <CategoryButtonFilter
              key="StoreSidebar.category"
              label={filterConfig.filterConfig?.label || 'Interest Area'}
              options={STORE_SIDEBAR_CATEGORIES}
              urlQueryParams={urlQueryParams}
              onParamChange={onParamChange}
            />
          );
        }
        return (
          <FilterComponent
            key={`StoreSidebar.${filterConfig.scope || 'built-in'}.${filterConfig.key}`}
            idPrefix="StoreSidebar"
            className={css.filterSection}
            config={filterConfig}
            marketplaceCurrency={config.currency}
            urlQueryParams={urlQueryParams}
            initialValues={getInitialValues}
            getHandleChangedValueFn={getHandleChangedValueFn}
            intl={intl}
            liveEdit
            showAsPopup={false}
            isDesktop
            initialIsOpen={false}
          />
        );
      })}

      {sidebarFilters.length > 0 && (
        <button className={css.resetAllButton} onClick={onResetAll}>
          Reset all filters
        </button>
      )}
    </aside>
  );
};

StoreSidebar.defaultProps = {
  isMobile: false,
  catalogMode: 'products',
};

StoreSidebar.propTypes = {
  urlQueryParams: object.isRequired,
  onParamChange: func.isRequired,
  onResetAll: func.isRequired,
  intl: object.isRequired,
  isMobile: bool,
  catalogMode: string,
};

export default StoreSidebar;
